import React from 'react';
import {
    Box,
    Chip,
    Typography
} from '@mui/material';

import {
    LocationOn as LocationOnIcon
} from '@mui/icons-material';

import {
    formatDate,
    formatMeters,
    formatTime
} from '../../utils/parsing';
import { FONT_FAMILY_SANS } from '../../theme';

type PropsType = {
    data: {
        firstPickupDate: string,
        lastPickupDate?: string | null,
        firstDeliveryDate?: string,
        lastDeliveryDate: string | null,
        firstPickupTime?: string | null,
        lastPickupTime?: string | null,
        firstDeliveryTime?: string | null,
        lastDeliveryTime?: string | null,
        drivingDistance: number | null,
        pickupCity: string,
        pickupAttention?: string | null,
        pickupStreet?: string | null,
        pickupZip?: string | null,
        deliveryCity: string
        deliveryAttention?: string | null,
        deliveryStreet?: string | null,
        deliveryZip?: string | null
    },
    small?: boolean
};

const LINE_HEIGHT = 4;

const citySx = (small?: boolean) => ({
    fontFamily: FONT_FAMILY_SANS,
    fontWeight: 600,
    letterSpacing: '-0.01em',
    fontSize: small ? '1.25rem' : '1.75rem',
    lineHeight: 1.25,
    wordBreak: 'break-word'
});

const addressSx = (small?: boolean) => ({
    fontFamily: FONT_FAMILY_SANS,
    fontWeight: 400,
    fontSize: small ? '0.875rem' : '1rem',
    lineHeight: 1.4,
    wordBreak: 'break-word'
});

const dateSx = (small?: boolean) => ({
    fontFamily: FONT_FAMILY_SANS,
    fontWeight: 600,
    letterSpacing: '-0.01em',
    fontSize: small ? '1rem' : '1.125rem',
    lineHeight: 1.3,
    wordBreak: 'break-word'
});

const DriveLine: React.FC<PropsType> = (props) => {
    const {
        data,
        small
    } = props;

    return (
        <Box sx={{ m: small ? 1 : 2, minWidth: 0 }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 2
                }}
            >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={citySx(small)}>
                        {data.pickupCity}
                    </Typography>
                    {(data.pickupStreet || data.pickupZip) && (
                        <Typography sx={addressSx(small)}>
                            {data.pickupStreet}
                            {data.pickupStreet && data.pickupZip && ', '}
                            {data.pickupZip}
                        </Typography>
                    )}
                    {data.pickupAttention && (
                        <Typography sx={addressSx(small)}>
                            {`Ref: ${data.pickupAttention}`}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1, textAlign: 'right' }}>
                    <Typography sx={citySx(small)}>
                        {data.deliveryCity}
                    </Typography>
                    {(data.deliveryStreet || data.deliveryZip) && (
                        <Typography sx={addressSx(small)}>
                            {data.deliveryStreet}
                            {data.deliveryStreet && data.deliveryZip && ', '}
                            {data.deliveryZip}
                        </Typography>
                    )}
                    {data.deliveryAttention && (
                        <Typography sx={addressSx(small)}>
                            {`Ref: ${data.deliveryAttention}`}
                        </Typography>
                    )}
                </Box>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    marginTop: 3,
                    marginBottom: 3,
                    marginLeft: { xs: 2, sm: 6 },
                    marginRight: { xs: 2, sm: 6 },
                    position: 'relative'
                }}
            >
                <Box
                    sx={{
                        height: LINE_HEIGHT,
                        marginLeft: 1,
                        marginRight: 1,
                        backgroundColor: 'primary.main',
                        position: 'relative'
                    }}
                />
                <Box
                    sx={{
                        height: LINE_HEIGHT * 3,
                        width: LINE_HEIGHT * 3,
                        borderRadius: (LINE_HEIGHT * 3) / 2,
                        position: 'absolute',
                        top: -LINE_HEIGHT,
                        left: 0,
                        backgroundColor: 'primary.main'
                    }}
                />
                <Box
                    sx={{
                        height: LINE_HEIGHT * 3,
                        width: LINE_HEIGHT * 3,
                        borderRadius: LINE_HEIGHT * (3 / 2),
                        borderColor: 'primary.main',
                        borderStyle: 'solid',
                        borderWidth: LINE_HEIGHT,
                        backgroundColor: 'white',
                        position: 'absolute',
                        top: -LINE_HEIGHT,
                        right: 0,
                    }}
                />
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 1
                }}
            >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    {data.lastPickupDate && (
                        <Typography variant="body2">
                            Från&nbsp;
                        </Typography>
                    )}

                    <Typography sx={dateSx(small)}>
                        {formatDate(data.firstPickupDate)}
                        {data.firstPickupTime ? `, ${formatTime(data.firstPickupTime)}` : ''}
                    </Typography>
                    {data.lastPickupDate && (
                        <>
                            <Typography variant="body2">
                                Till&nbsp;
                            </Typography>
                            <Typography sx={dateSx(small)}>
                                {formatDate(data.lastPickupDate)}
                                {data.lastPickupTime ? `, ${formatTime(data.lastPickupTime)}` : ''}
                            </Typography>
                        </>
                    )}
                </Box>
                <Box sx={{ flex: '0 0 auto', alignSelf: 'center' }}>
                    {(typeof data.drivingDistance === 'number' || data.drivingDistance) && (
                        <Chip
                            size={small ? 'small' : 'medium'}
                            sx={{ m: { xs: 0, sm: 1 } }}
                            icon={<LocationOnIcon />}
                            label={formatMeters(data.drivingDistance)}
                        />
                    )}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1, textAlign: 'right' }}>
                    {(data.firstDeliveryDate && !data.lastDeliveryDate) && (
                        <Typography sx={dateSx(small)}>
                            {formatDate(data.firstDeliveryDate)}
                            {data.firstDeliveryTime ? `, ${formatTime(data.firstDeliveryTime)}` : ''}
                        </Typography>
                    )}
                    {(!data.firstDeliveryDate && data.lastDeliveryDate) && (
                        <Typography sx={dateSx(small)}>
                            {formatDate(data.lastDeliveryDate)}
                            {data.lastDeliveryTime ? `, ${formatTime(data.lastDeliveryTime)}` : ''}
                        </Typography>
                    )}
                    {(data.firstDeliveryDate && data.lastDeliveryDate) && (
                        <>
                            <Typography variant="body2">
                                Från&nbsp;
                            </Typography>
                            <Typography sx={dateSx(small)}>
                                {formatDate(data.firstDeliveryDate)}
                                {data.firstDeliveryTime ? `, ${formatTime(data.firstDeliveryTime)}` : ''}
                            </Typography>
                            <Typography variant="body2">
                                Till&nbsp;
                            </Typography>
                            <Typography sx={dateSx(small)}>
                                {formatDate(data.lastDeliveryDate)}
                                {data.lastDeliveryTime ? `, ${formatTime(data.lastDeliveryTime)}` : ''}
                            </Typography>
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default DriveLine;
